import { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { AuthProvider } from '@/core/AuthContext';
import { ThemeProvider } from '@/core/ThemeContext';
import { ToastProvider } from '@/core/ToastContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { AppLayout } from './components/layout/AppLayout';
import { PremiumGate } from './components/PremiumGate';
import { GlobalModals } from './components/GlobalModals';
import { Landing } from './pages/Landing';
import { TrainerLanding } from './pages/TrainerLanding';
import { LoginPage } from './pages/LoginPage';
import { JoinTrainer } from './pages/JoinTrainer';
import { ClientPhoneLoginPage } from './pages/ClientPhoneLoginPage';
import { TrainerMagicLogin } from './pages/TrainerMagicLogin';
import { TrainerPhoneLoginPage } from './pages/TrainerPhoneLoginPage';
import { ClientMagicLogin } from './pages/ClientMagicLogin';
import { Debug } from './pages/Debug';
import { DebugScreenshots } from './pages/DebugScreenshots';
import { ClientDashboard } from './pages/ClientDashboard';
import { Train } from './pages/client/Train';
import { Progress } from './pages/client/Progress';
import { ExerciseProgress } from './pages/client/ExerciseProgress';
import { PostureCheckView } from './pages/client/PostureCheckView';
import { ProgressGalleryView } from './pages/client/ProgressGalleryView';
import { CompareView } from './pages/client/CompareView';
import { TrainerContact } from './pages/TrainerContact';
import { TrainerDashboard } from './pages/TrainerDashboard';
import { TrainerOnboarding } from './pages/TrainerOnboarding';
import { Clients } from './pages/trainer/Clients';
import { TrainerClientDetail } from './pages/TrainerClientDetail';
import { Exercises } from './pages/trainer/Exercises';
import { Workouts } from './pages/trainer/Workouts';
import { SessionView } from './pages/SessionView';
import { AdminPanel } from './pages/AdminPanel';
import { Settings } from './pages/Settings';
import { OnboardingWizard } from './components/onboarding';
import { BookIntroSession } from './pages/BookIntroSession';
import { initNotifications } from '@/core/notifications';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import Paywall from './pages/Paywall';
import Beta from './pages/Beta';
import './App.css';

// Lazy load heavy 3D components to reduce initial bundle size
const BodyModelDemo = lazy(() => import('./pages/BodyModelDemo'));

function App() {
  useEffect(() => {
    void initNotifications();
  }, []);

  return (
    <BrowserRouter unstable_useTransitions={false}>
      <ThemeProvider>
        <ToastProvider>
          <AuthProvider>
            <Routes>
            {/* Landing Page for logged-out users */}
            <Route path="/" element={<Landing />} />

            {/* Public Routes */}
            <Route path="/trainers" element={<TrainerLanding />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/join/:code" element={<JoinTrainer />} />
            <Route path="/phone-login" element={<ClientPhoneLoginPage />} />
            <Route path="/trainer-auth" element={<TrainerPhoneLoginPage />} />
            <Route path="/trainer/signup" element={<Navigate to="/trainer-auth" replace />} />
            <Route path="/trainer/login" element={<Navigate to="/trainer-auth" replace />} />
            <Route path="/trainer-signup" element={<Navigate to="/trainer-auth" replace />} />
            <Route path="/trainer-login" element={<Navigate to="/trainer-auth" replace />} />
            <Route path="/t/:token" element={<TrainerMagicLogin />} />
            <Route path="/c/:token" element={<ClientMagicLogin />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/pricing" element={<Paywall />} />
            <Route path="/paywall" element={<Paywall />} />
            <Route path="/beta" element={<Beta />} />
            
            {/* Debug Routes (Development Only - Do not link from UI) */}
            <Route path="/debug" element={<Debug />} />
            <Route path="/debug/screenshots" element={<DebugScreenshots />} />

            {/* Onboarding Routes */}
            <Route
              path="/onboarding"
              element={
                <ProtectedRoute>
                  <OnboardingWizard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/book-intro"
              element={
                <ProtectedRoute>
                  <BookIntroSession />
                </ProtectedRoute>
              }
            />

            {/* Client Routes */}
            <Route
              path="/client"
              element={
                <ProtectedRoute requiredRole="client">
                  <AppLayout>
                    <PremiumGate>
                      <ClientDashboard />
                    </PremiumGate>
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/client/train"
              element={
                <ProtectedRoute requiredRole="client">
                  <AppLayout>
                    <PremiumGate>
                      <Train />
                    </PremiumGate>
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/client/progress"
              element={
                <ProtectedRoute requiredRole="client">
                  <AppLayout>
                    <PremiumGate>
                      <Progress />
                    </PremiumGate>
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/client/progress/exercises"
              element={
                <ProtectedRoute requiredRole="client">
                  <AppLayout>
                    <PremiumGate>
                      <ExerciseProgress />
                    </PremiumGate>
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/client/progress/photos"
              element={
                <ProtectedRoute requiredRole="client">
                  <AppLayout>
                    <PremiumGate>
                      <ProgressGalleryView />
                    </PremiumGate>
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/client/progress/photos/compare"
              element={
                <ProtectedRoute requiredRole="client">
                  <AppLayout>
                    <PremiumGate>
                      <CompareView />
                    </PremiumGate>
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/client/progress/photos/capture"
              element={
                <ProtectedRoute requiredRole="client">
                  <PremiumGate>
                    <PostureCheckView />
                  </PremiumGate>
                </ProtectedRoute>
              }
            />
            <Route
              path="/client/settings"
              element={
                <ProtectedRoute requiredRole="client">
                  <AppLayout>
                    <PremiumGate>
                      <Settings />
                    </PremiumGate>
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/client/trainer-contact"
              element={
                <ProtectedRoute requiredRole="client">
                  <PremiumGate>
                    <TrainerContact />
                  </PremiumGate>
                </ProtectedRoute>
              }
            />
            <Route
              path="/session/:sessionId"
              element={
                <ProtectedRoute requiredRole="client">
                  <PremiumGate>
                    <SessionView />
                  </PremiumGate>
                </ProtectedRoute>
              }
            />

            {/* Trainer Routes */}
            <Route
              path="/trainer/onboarding"
              element={
                <ProtectedRoute requiredRole="trainer">
                  <TrainerOnboarding />
                </ProtectedRoute>
              }
            />
            <Route
              path="/trainer"
              element={
                <ProtectedRoute requiredRole="trainer">
                  <AppLayout>
                    <TrainerDashboard />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/trainer/exercises"
              element={
                <ProtectedRoute requiredRole="trainer">
                  <AppLayout>
                    <Exercises />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/trainer/workouts"
              element={
                <ProtectedRoute requiredRole="trainer">
                  <AppLayout>
                    <Workouts />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/trainer/clients"
              element={
                <ProtectedRoute requiredRole="trainer">
                  <AppLayout>
                    <Clients />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/trainer/clients/:clientId"
              element={
                <ProtectedRoute requiredRole="trainer">
                  <AppLayout>
                    <TrainerClientDetail />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            {/* Redirect old sessions route to clients */}
            <Route
              path="/trainer/sessions"
              element={<Navigate to="/trainer/clients" replace />}
            />
            <Route
              path="/trainer/settings"
              element={
                <ProtectedRoute requiredRole="trainer">
                  <AppLayout>
                    <Settings />
                  </AppLayout>
                </ProtectedRoute>
              }
            />

            {/* Admin Route */}
            <Route
              path="/admin"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <AdminPanel />
                  </AppLayout>
                </ProtectedRoute>
              }
            />

            {/* Dev/Demo Routes (lazy loaded) */}
            <Route
              path="/demo/body-model"
              element={
                <Suspense fallback={
                  <div className="min-h-screen bg-slate-900 flex items-center justify-center">
                    <div className="text-white text-lg">Loading 3D Model...</div>
                  </div>
                }>
                  <BodyModelDemo />
                </Suspense>
              }
            />

            {/* Fallback */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <GlobalModals />
          </AuthProvider>
        </ToastProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
